import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const normalizeAuthResult = (result) => {
  // Accept a variety of shapes and return { user, session, error, raw }
  const raw = result;

  // If the auth layer throws, caller's try/catch will handle
  if (!result) return { user: null, session: null, error: null, raw };

  // Supabase v2 shape
  if (typeof result === 'object' && 'data' in result) {
    const { data, error } = result;
    return {
      user: data?.user ?? null,
      session: data?.session ?? null,
      error: error ?? null,
      raw,
    };
  }

  // { user, error } shape
  if ('user' in result || 'error' in result) {
    return {
      user: result?.user ?? null,
      session: result?.session ?? null,
      error: result?.error ?? null,
      raw,
    };
  }

  // Plain user object return
  if (result && typeof result === 'object' && result?.id && result?.email) {
    return { user: result, session: null, error: null, raw };
  }

  return { user: null, session: null, error: null, raw };
};

const LoginForm = ({ onSuccess, error, setError }) => {
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    const email = (formData?.email || '')?.trim();
    const password = (formData?.password || '')?.trim();

    if (!email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/?.test(email)) newErrors.email = 'Please enter a valid email address';

    if (!password) newErrors.password = 'Password is required';
    else if (password?.length < 6) newErrors.password = 'Password must be at least 6 characters';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target || {};
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? !!checked : value,
    }));

    if (name && errors?.[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (isLoading) return;

    if (!validateForm()) return;

    setIsLoading(true);
    setError('');

    const email = formData?.email?.trim()?.toLowerCase();
    const password = formData?.password?.trim();

    try {
      // Try both common signatures:
      // signIn(email, password) OR signIn({ email, password })
      let result;
      try {
        result = await signIn(email, password);
      } catch {
        // If your signIn expects an object
        result = await signIn({ email, password });
      }

      const { user, session, error: authError, raw } = normalizeAuthResult(result);

      // Helpful debug while you're wiring this up
      // eslint-disable-next-line no-console
      console.log('Auth result (normalized):', { hasUser: !!user, hasSession: !!session, authError, raw });

      if (authError) {
        // Supabase often uses 'Invalid login credentials' string; surface it plainly
        const msg =
          authError?.message ||
          authError?.error_description ||
          'Invalid email or password. Please check your credentials and try again.';
        setError(msg);
        return;
      }

      if (!user && !session) {
        setError('Login failed. Please try again.');
        return;
      }

      if (formData?.rememberMe) {
        try {
          localStorage.setItem('rememberMe', 'true');
        } catch {}
      }

      // Success
      onSuccess?.();
    } catch (err) {
      // Network / thrown error from auth lib
      // eslint-disable-next-line no-console
      console.error('Login error (catch):', err);
      const msg =
        err?.message ||
        err?.error_description ||
        'Network or server error. Please try again.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    alert('Password reset functionality would be implemented here');
  };

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* General Error */}
        {error && (
          <div className="p-4 bg-red-50/90 backdrop-blur-sm border border-red-200/60 rounded-2xl animate-slide-up">
            <div className="flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 mb-1">Authentication Error</p>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Email Field with Enhanced Styling */}
        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-semibold text-slate-700 block">
            Email Address
          </label>
          <div className="relative">
            <Input
              id="email"
              type="email"
              name="email"
              placeholder="Enter your email address"
              value={formData?.email}
              onChange={handleInputChange}
              error={errors?.email}
              required
              disabled={isLoading}
              className="bg-white/80 backdrop-blur-sm border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20 focus:bg-white rounded-2xl h-14 text-base transition-all duration-300 hover:bg-white/90 hover:shadow-md pl-12"
            />
            <Icon name="Mail" size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" />
          </div>
        </div>

        {/* Password Field with Enhanced Styling and Toggle */}
        <div className="space-y-2">
          <label htmlFor="password" className="text-sm font-semibold text-slate-700 block">
            Password
          </label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Enter your password"
              value={formData?.password}
              onChange={handleInputChange}
              error={errors?.password}
              required
              disabled={isLoading}
              className="bg-white/80 backdrop-blur-sm border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20 focus:bg-white rounded-2xl h-14 text-base transition-all duration-300 hover:bg-white/90 hover:shadow-md pl-12 pr-12"
            />
            <Icon name="Lock" size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 rounded-lg p-1"
              disabled={isLoading}
            >
              <Icon name={showPassword ? "EyeOff" : "Eye"} size={20} />
            </button>
          </div>
        </div>

        {/* Enhanced Remember Me & Forgot Password */}
        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center space-x-3">
            <Checkbox
              id="rememberMe"
              name="rememberMe"
              checked={formData?.rememberMe}
              onChange={handleInputChange}
              disabled={isLoading}
              className="rounded-lg focus:ring-indigo-500 focus:ring-offset-0 text-indigo-600 h-5 w-5"
            />
            <label 
              htmlFor="rememberMe" 
              className="text-sm font-medium text-slate-700 cursor-pointer select-none hover:text-slate-900 transition-colors duration-200"
            >
              Keep me signed in
            </label>
          </div>
          <button
            type="button"
            onClick={handleForgotPassword}
            className="text-sm font-semibold text-indigo-600 hover:text-indigo-500 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-lg px-2 py-1"
            disabled={isLoading}
          >
            Forgot password?
          </button>
        </div>

        {/* Enhanced Submit Button */}
        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isLoading}
          disabled={isLoading}
          className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold h-14 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none mt-8"
        >
          {isLoading ? (
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
              <span className="text-lg">Signing you in...</span>
            </div>
          ) : (
            <div className="flex items-center justify-center space-x-3">
              <Icon name="ArrowRight" size={20} />
              <span className="text-lg">Sign In to Your Workspace</span>
            </div>
          )}
        </Button>

        {/* Enhanced Alternative Sign-In Methods */}
        <div className="mt-10">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200/60"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-6 bg-white/90 text-slate-600 font-medium rounded-full">
                Or continue with
              </span>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4">
            <button
              type="button"
              className="group inline-flex w-full justify-center items-center px-6 py-4 border border-slate-200/60 rounded-2xl bg-white/80 backdrop-blur-sm text-sm font-semibold text-slate-700 hover:bg-white hover:border-slate-300 hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
              disabled={isLoading}
            >
              <Icon name="Mail" size={20} className="mr-3 text-red-500 group-hover:scale-110 transition-transform duration-200" />
              Google SSO
            </button>

            <button
              type="button"
              className="group inline-flex w-full justify-center items-center px-6 py-4 border border-slate-200/60 rounded-2xl bg-white/80 backdrop-blur-sm text-sm font-semibold text-slate-700 hover:bg-white hover:border-slate-300 hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
              disabled={isLoading}
            >
              <Icon name="Building" size={20} className="mr-3 text-blue-600 group-hover:scale-110 transition-transform duration-200" />
              Enterprise SSO
            </button>
          </div>

          {/* Additional Security Information */}
          <div className="mt-8 p-4 bg-slate-50/80 backdrop-blur-sm rounded-2xl border border-slate-200/50">
            <div className="flex items-center justify-center space-x-2 text-slate-600">
              <Icon name="Shield" size={16} className="text-green-600" />
              <span className="text-sm font-medium">Protected by enterprise-grade security</span>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;